import { Component, ChangeDetectionStrategy } from '@angular/core';

@Component({
  selector: 'app-privacy',
  template: `
    <div class="privacy-container">
      <div class="privacy-card">
        <h1>Privacy Policy - Litmus Learn &amp; Hey Lumos</h1>
        <p class="update-date"><strong>Last Updated:</strong> April 13, 2025</p>

        <!-- Introduction -->
        <section id="introduction">
          <h2>1. Introduction</h2>
          <p>
            This Privacy Policy (“Policy”) applies to the Litmus Learn website,
            mobile applications, and services, and the Hey Lumos website and
            mobile applications (collectively, the “Litmus Learn Materials”).
            Litmus Learn and Hey Lumos are products and brands of Dhrona EdTech
            Private Ltd (“Litmus Learn”, “we”, “us”, or “our”).
          </p>
          <p>
            This Policy explains what information we collect, how we use it and
            with whom we share it. It does not apply to third‐party websites or
            services that you might access via links on our pages – those have
            their own privacy policies.
          </p>
          <p>
            By using our applications, services, or products, you acknowledge
            that you have read, understood, and agree to the terms of this
            Privacy Policy.
          </p>
        </section>

        <!-- Compliance with Privacy Laws -->
        <section id="compliance">
          <h2>2. Compliance With Privacy Laws</h2>
          <p>
            Litmus Learn adheres to applicable Data Protection Laws, including
            the Information Technology Act, 2000 (“IT Act”), the Information
            Technology (Reasonable Security Practices and Procedures and
            Sensitive Personal Data or Information) Rules, 2011 (“IT Rules”),
            and the Information Technology (Intermediaries Guidelines and
            Digital Media Ethics Code) Rules, 2021.
          </p>
        </section>

        <!-- Definitions -->
        <section id="definitions">
          <h2>3. Definitions</h2>
          <ol>
            <li>
              <strong>Litmus Learn Materials:</strong>
              Refers to the Litmus Learn website, mobile applications, and all
              content created, curated, owned, and operated by Litmus Learn.
            </li>
            <li>
              <strong>Litmus Learn User:</strong>
              Includes institutions, teacher users, student users, and for
              students, the parents/legal guardians of such users.
            </li>
            <li>
              <strong>Institution:</strong>
              Means any school or educational institution that enters into a
              contractual agreement with Litmus Learn to adopt and use the
              Litmus Learn Materials.
            </li>
            <li>
              <strong>Personal Information:</strong>
              Refers to any data that can be used to identify a particular user,
              as defined by applicable data protection laws.
            </li>
            <li>
              <strong>Sensitive Personal Data or Information:</strong>
              Means personal information that is protected under the IT Act and
              associated rules.
            </li>
            <li>
              <strong>Student Litmus Learn User:</strong>
              Is a user who is a student of an institution, has a valid account,
              and accesses the Litmus Learn Materials as provided by a teacher
              or directly by Litmus Learn.
            </li>
            <li>
              <strong>Teacher Litmus Learn User:</strong>
              Refers to an individual who is employed in an institution, has a
              valid teacher account, and uses Litmus Learn Materials to deliver
              educational content.
            </li>
            <li>
              <strong>Terms and Conditions:</strong>
              Means the Litmus Learn Terms and Conditions that govern your use
              of the Litmus Learn Materials and/or Services.
            </li>
          </ol>
        </section>

        <!-- Why We Collect Information -->
        <section id="purpose">
          <h2>4. Why Does Litmus Learn Collect Personal Information?</h2>
          <p>
            We collect personal information to provide you with the best
            possible experience and to ensure that our services work
            efficiently. The information is used for purposes that include, but
            are not limited to:
          </p>
          <ul>
            <li>Creating and managing user accounts.</li>
            <li>
              Personalizing your experience on our website and applications.
            </li>
            <li>Providing teachers with analytics on student progress.</li>
            <li>Sending important email updates and notifications.</li>
            <li>Assessing and improving the quality of our services.</li>
            <li>Facilitating teacher collaboration and content creation.</li>
            <li>Securing and safeguarding personal information.</li>
            <li>Complying with all applicable data protection laws.</li>
          </ul>
        </section>

        <!-- What Information We Collect -->
        <section id="collection">
          <h2>5. What Information Do We Collect?</h2>
          <p>
            We do not automatically collect personal information. We collect
            data only when you provide it to us voluntarily or as required
            during registration or use of the service. Depending on your role,
            we may collect:
          </p>
          <h3>a. Website/Application Visitors</h3>
          <p>
            When you visit our website or use our application without
            registering, we automatically collect non-personally identifiable
            information, including your IP address, device identifier, browser
            type, and usage data (such as the duration of your visit and pages
            viewed).
          </p>
          <h3>b. Litmus Learn User Account</h3>
          <p>
            For users with an account, the information collected includes the
            data you provide during registration, such as your name, email
            address, contact number, institution name, and profile photo
            (optional).
          </p>
          <h3>c. Personal Information of Teacher Litmus Learn Users</h3>
          <p>
            Teachers voluntarily provide personal information during
            registration, including their full name, email address, contact
            information, the institution they work for, subjects taught, and any
            data generated through the use of our platform.
          </p>
          <h3>d. Personal Information of Student Litmus Learn Users</h3>
          <p>
            For student users, registration requires personal information such
            as the student’s name, unique identifier (e.g., roll number), class,
            section, institution name, and the email address of the parent or
            legal guardian. Additionally, we may collect data regarding their
            academic interactions (e.g., quiz responses, assignment submissions,
            participation in interactive sessions).
          </p>
          <p>
            <strong>Note on Minors:</strong> For student Litmus Learn users
            under the age of 18, we require consent from a parent or legal
            guardian. The personal information of minors is handled with extra
            care and in accordance with the law.
          </p>
        </section>

        <!-- Basis for Processing Information -->
        <section id="processing">
          <h2>6. Basis for Processing Information</h2>
          <p>
            Authorization is provided to Litmus Learn to collect, process, and
            handle your personal information under the following circumstances:
          </p>
          <ul>
            <li>
              When an Institution enters into a contractual agreement with us
              and purchases Litmus Learn Materials.
            </li>
            <li>
              When a Teacher Litmus Learn User registers to use the platform.
            </li>
            <li>
              When a parent or legal guardian consents to register a Student
              Litmus Learn User.
            </li>
          </ul>
        </section>

        <!-- Information Collected through Litmus Learn Materials -->
        <section id="automated">
          <h2>7. Information Collected Through Litmus Learn Materials</h2>
          <p>
            When you access the Litmus Learn Materials, our servers
            automatically collect certain data from your device. This includes:
          </p>
          <p>
            <strong>For Website/Application Visitors:</strong> Non-personally
            identifiable information such as IP address, device identifier,
            browser details, the time you spent on the site, and interactions
            with the site.
          </p>
          <p>
            <strong>For Registered Users:</strong> Information provided during
            registration and as part of your ongoing use of the platform (as
            described above).
          </p>
        </section>

        <!-- Children's Information Visibility -->
        <section id="children">
          <h2>8. Children's Information</h2>
          <p>
            No Litmus Learn user’s full profile is made publicly available. For
            student users, only limited information (e.g., first name, class
            details, and related academic data) may be visible to teachers and
            other students as allowed by classroom settings. Teachers and
            institutions are responsible for ensuring that student information
            is handled responsibly.
          </p>
        </section>

        <!-- Web Server Host Information -->
        <!-- Web Server Host Information -->
        <section id="webserver">
          <h2>9. Web Server Host Information</h2>
          <p>
            Litmus Learn uses third-party cloud hosting providers—such as Amazon
            Web Services (AWS), Microsoft Azure, and Google Cloud Platform
            (GCP)—to host and store its content. For more details on how these
            providers handle privacy, please review their privacy policies:
          </p>
          <ul>
            <li>
              AWS:
              <a href="https://aws.amazon.com/privacy" target="_blank"
                >https://aws.amazon.com/privacy</a
              >
              and
              <a
                href="https://aws.amazon.com/compliance/data-privacy-faq/"
                target="_blank"
                >AWS Data Privacy FAQ</a
              >
            </li>
            <li>
              Microsoft Azure:
              <a
                href="https://privacy.microsoft.com/en-us/privacystatement"
                target="_blank"
                >https://privacy.microsoft.com/en-us/privacystatement</a
              >
            </li>
            <li>
              Google Cloud Platform:
              <a
                href="https://cloud.google.com/terms/cloud-privacy-notice"
                target="_blank"
                >https://cloud.google.com/terms/cloud-privacy-notice</a
              >
            </li>
          </ul>
        </section>

        <!-- Sharing Information with Third Parties -->
        <section id="sharing">
          <h2>10. Does Litmus Learn Share Information With Third Parties?</h2>
          <p>
            Except as disclosed at the time of collection or as required by law,
            we do not share your personal information with third parties.
            Information may be shared:
          </p>
          <ul>
            <li>
              With our employees who are authorized to use your information
              solely for providing and improving our services.
            </li>
            <li>
              When required to comply with legal obligations, or to protect our
              legal rights.
            </li>
            <li>
              With our partners only for purposes of providing added value to
              our services. We do not sell, rent, or trade your personal
              information to third parties.
            </li>
          </ul>
        </section>

        <!-- Confidentiality and Security -->
        <section id="security">
          <h2>11. Confidentiality and Security</h2>
          <p>
            We take all reasonable precautions to protect your personally
            identifiable information from unauthorized access, alteration,
            disclosure, or destruction, both online and offline.
          </p>
        </section>

        <!-- Changes to the Privacy Policy -->
        <section id="changes">
          <h2>12. Changes to the Privacy Policy</h2>
          <p>
            We reserve the right to modify this Privacy Policy at any time to
            comply with applicable laws or for other operational reasons. Please
            review this page periodically to stay informed of any updates.
            Material changes will be communicated via email (if applicable) and
            by a notice on our website.
          </p>
        </section>

        <!-- Data Protection After Contract Termination -->
        <section id="termination">
          <h2>13. Data Protection After Contract Termination</h2>
          <p>
            In the event of termination, cancellation, or expiration of an
            agreement with an institution, Litmus Learn agrees to delete or
            destroy all personal data belonging to the institution’s users
            within 90 days.
          </p>
        </section>

        <!-- Disclaimer -->
        <section id="disclaimer">
          <h2>14. Disclaimer</h2>
          <p>
            While we are committed to protecting your privacy, we cannot
            guarantee that all private communications and personal information
            will remain confidential in every instance. You acknowledge that you
            assume the risk for information you share online.
          </p>
        </section>

        <!-- How to Contact Litmus Learn -->
        <section id="contact">
          <h2>15. How to Contact Litmus Learn</h2>
          <p>
            If you have any questions or concerns regarding this Privacy Policy,
            please contact us:
          </p>
          <p>
            <strong>Email:</strong>
            <a href="mailto:contact@litmuslearn.com">contact@litmuslearn.com</a>
          </p>
          <p>
            <strong>Address:</strong> Dhrona EdTech Private Ltd, 1st Floor
            Hanush, 5/3 Vishwanathan Street, West Mambalam, Chennai, 600033,
            Tamil Nadu, India.
          </p>
          <p>
            In accordance with the Information Technology (Intermediary
            Guidelines and Digital Media Ethics Code) Rules, 2021, our Grievance
            Officer is:
          </p>
          <p>
            <strong>Grievance Officer:</strong> Mr. Murali, 1st Floor Hanush,
            5/3 Vishwanathan Street, West Mambalam, Chennai, 600033, Tamil Nadu.
            <br />
            <strong>Email:</strong>
            <a href="mailto:contact@litmuslearn.com">contact@litmuslearn.com</a>
          </p>
          <p>
            <strong>Data Protection Officer:</strong> Mr. Murali, same address
            as above. <br />
            <strong>Email:</strong>
            <a href="mailto:contact@litmuslearn.com">contact@litmuslearn.com</a>
          </p>
        </section>
      </div>
    </div>

    <!-- Decorative Waves Background -->
    <div class="waves">
      <svg
        viewBox="0 0 1516 279"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0)" fill="#cb4242">
          <path
            d="M0 278.958V43.68C54.268 59.974 108.536 76.27 188.447 73.09c79.912-3.178 185.467-25.829 258.984-27.645 73.516-1.816 114.992 17.203 171.602 14.117 56.61-3.087 128.353-28.279 208.45-21.764 80.098 6.516 168.549 44.738 251.617 37.645 83.06-7.093 160.74-59.502 232.66-72.348 71.92-12.846 138.08 13.87 204.24 40.586v235.277H0z"
            fill-opacity=".5"
          />
          <path
            d="M0 278.959V161.32c55.471-5.12 110.943-10.24 182.131-2.353 71.187 7.887 158.092 28.78 244.244 18.234 86.152-10.546 171.553-52.532 252.667-47.056 81.113 5.476 157.941 58.414 213.714 57.055 55.773-1.358 90.491-57.013 160.024-67.642 69.53-10.629 173.87 23.768 256.88 38.232 83 14.465 144.67 8.997 206.34 3.53v117.639H0z"
          />
        </g>
        <defs>
          <clipPath id="clip0">
            <path fill="#fff" d="M0 0h1516v279H0z" />
          </clipPath>
        </defs>
      </svg>
    </div>
  `,
  styles: [
    `
      .privacy-container {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background-color: var(--soul, #f5f5f5);
        padding: 1rem;
        position: relative;
      }
      .privacy-card {
        background: #ffffff;
        max-width: 800px;
        width: 100%;
        padding: 2rem;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        overflow-y: auto;
        max-height: 90vh;
        position: relative;
        z-index: 1;
      }
      h1,
      h2,
      h3 {
        color: #333;
        margin-top: 0;
      }
      h1 {
        font-size: 2rem;
        margin-bottom: 0.5rem;
      }
      h2 {
        font-size: 1.5rem;
        margin-bottom: 0.5rem;
        border-bottom: 1px solid #ddd;
        padding-bottom: 0.3rem;
      }
      h3 {
        font-size: 1.25rem;
        margin-bottom: 0.5rem;
      }
      p,
      li {
        font-size: 1rem;
        line-height: 1.6;
        color: #555;
        margin-bottom: 1rem;
      }
      ul,
      ol {
        margin: 0 0 1rem 1.5rem;
      }
      .update-date {
        font-style: italic;
        margin-bottom: 2rem;
      }
      section {
        margin-bottom: 2rem;
      }
      a {
        color: #1a73e8;
        text-decoration: none;
      }
      a:hover {
        text-decoration: underline;
      }
      .waves {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 0;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivacyComponent {}
